import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { BUSINESS } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll reply shortly.");
      setForm({ full_name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-[#E5E0D8] bg-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <p className="overline mb-5">Say hello</p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl text-text-main leading-tight">
              Questions? A quick idea?
              <br />
              <span className="italic text-cedar">Drop us a note.</span>
            </h2>
            <p className="text-text-muted mt-6 max-w-md leading-relaxed">
              Not ready for a full quote? We&apos;re happy to answer any question — about a sauna model you&apos;re
              considering, site prep, or timelines.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={`mailto:${BUSINESS.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 group"
              >
                <span className="w-11 h-11 flex items-center justify-center border border-[#E5E0D8] rounded-full text-cedar group-hover:bg-cedar group-hover:text-white transition-colors">
                  <Mail size={16} strokeWidth={1.5} />
                </span>
                <span className="font-accent text-text-main group-hover:text-cedar">{BUSINESS.email}</span>
              </a>
              <a
                href={`tel:${BUSINESS.phone}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 group"
              >
                <span className="w-11 h-11 flex items-center justify-center border border-[#E5E0D8] rounded-full text-cedar group-hover:bg-cedar group-hover:text-white transition-colors">
                  <Phone size={16} strokeWidth={1.5} />
                </span>
                <span className="font-accent text-text-main group-hover:text-cedar">{BUSINESS.phone}</span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center border border-[#E5E0D8] rounded-full text-cedar">
                  <MapPin size={16} strokeWidth={1.5} />
                </span>
                <span className="font-accent text-text-main">{BUSINESS.serviceArea}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-[#E5E0D8] bg-bone p-8 md:p-10 rounded-2xl space-y-6"
            >
              <div>
                <label className="overline block mb-3">Name</label>
                <input
                  data-testid="contact-input-name"
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                  className="editorial-input"
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <label className="overline block mb-3">Email</label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="editorial-input"
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div>
                <label className="overline block mb-3">Message</label>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  rows={5}
                  className="editorial-input resize-none"
                  placeholder="How can we help?"
                  required
                />
              </div>
              <div className="flex items-center justify-end">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-2 rounded-full bg-cedar text-white hover:bg-cedar-hover disabled:opacity-60 px-7 py-3.5 font-accent text-sm tracking-wide transition-colors"
                >
                  {sending ? "Sending…" : "Send message"}
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
